import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, type Stripe, type StripeElementsOptions } from '@stripe/stripe-js'
import { CheckCircle2, CreditCard, ExternalLink, Loader2, ReceiptText } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { ordersApi } from '@/services/orders'

const stripePromiseCache = new Map<string, Promise<Stripe | null>>()

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeGateway = (value?: string | number | null) => String(value ?? '').trim().toLowerCase()

const getStripePromise = (publishableKey: string) => {
  const cached = stripePromiseCache.get(publishableKey)
  if (cached) return cached
  const promise = loadStripe(publishableKey)
  stripePromiseCache.set(publishableKey, promise)
  return promise
}

type StripeOrderPaymentFormProps = {
  onPaymentSucceeded: (paymentReferenceId: string) => Promise<void>
  paymentReferenceId?: string | null
}

function StripeOrderPaymentForm({ onPaymentSucceeded, paymentReferenceId }: StripeOrderPaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })

      if (result.error) {
        const errorWithIntent = result.error as typeof result.error & {
          payment_intent?: { id?: string; status?: string }
        }
        if (result.error.code === 'payment_intent_unexpected_state' && errorWithIntent.payment_intent?.status === 'succeeded') {
          await onPaymentSucceeded(errorWithIntent.payment_intent.id || paymentReferenceId || '')
          return
        }
        toast.error(result.error.message || 'Payment was not completed.')
        return
      }

      if (result.paymentIntent?.status === 'succeeded') {
        await onPaymentSucceeded(result.paymentIntent.id || paymentReferenceId || '')
        return
      }
      toast.info(`Payment status: ${result.paymentIntent?.status || 'pending'}`)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <PaymentElement />
      <Button className="w-full" size="lg" type="submit" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
        Pay now
      </Button>
    </form>
  )
}

export default function PublicOrderPaymentPage() {
  const { orderId, paymentId, token: pathToken } = useParams<{ orderId?: string; paymentId?: string; token?: string }>()
  const [searchParams] = useSearchParams()
  const token = pathToken || searchParams.get('token') || ''
  const returnedPaymentIntent = searchParams.get('payment_intent')
  const handledReturnPaymentIntent = useRef<string | null>(null)
  const [confirmationPending, setConfirmationPending] = useState(false)
  const [pendingPaymentReferenceId, setPendingPaymentReferenceId] = useState<string | null>(null)
  const confirmedPaymentReferenceId = useRef<string | null>(null)

  const paymentQuery = useQuery({
    queryKey: ['public-order-payment', orderId, paymentId, token],
    queryFn: () => orderId && paymentId
      ? ordersApi.getPublicOrderPaymentDetail(orderId, paymentId, token)
      : ordersApi.getPublicOrderPaymentDetailByToken(token),
    enabled: Boolean(token && ((orderId && paymentId) || pathToken)),
    refetchInterval: confirmationPending ? 3000 : false,
    retry: false,
  })

  const paymentDetail = paymentQuery.data
  const order = paymentDetail?.order
  const payment = paymentDetail?.payment
  const paid = Boolean(paymentDetail?.paid || order?.is_paid)
  const gateway = normalizeGateway(payment?.payment_gateway_name || payment?.payment_gateway)
  const isStripe = gateway === 'stripe' || gateway === '200'
  const isCashFree = gateway === 'cashfree' || gateway === 'cash_free' || gateway === '100'
  const stripeClientSecret = isStripe ? payment?.signature : null
  const stripePublishableKey = isStripe ? payment?.publishable_key : null

  const stripePromise = useMemo(
    () => stripePublishableKey ? getStripePromise(stripePublishableKey) : null,
    [stripePublishableKey],
  )

  const stripeElementsOptions = useMemo<StripeElementsOptions | undefined>(
    () => stripeClientSecret ? { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' } } : undefined,
    [stripeClientSecret],
  )

  const waitForGatewayConfirmation = async (paymentReferenceId: string) => {
    setConfirmationPending(true)
    setPendingPaymentReferenceId(paymentReferenceId || payment?.payment_reference_id || null)
    toast.info('Payment completed. Waiting for gateway confirmation.')
    await paymentQuery.refetch()
  }

  useEffect(() => {
    if (!confirmationPending || !paid) return
    setConfirmationPending(false)
    const referenceId = pendingPaymentReferenceId || payment?.payment_reference_id || ''
    if (confirmedPaymentReferenceId.current === referenceId) return
    confirmedPaymentReferenceId.current = referenceId
    toast.success(referenceId ? `Payment confirmed. Reference ${referenceId}.` : 'Payment confirmed.')
  }, [confirmationPending, paid, pendingPaymentReferenceId, payment?.payment_reference_id])

  useEffect(() => {
    if (!returnedPaymentIntent || handledReturnPaymentIntent.current === returnedPaymentIntent) return
    handledReturnPaymentIntent.current = returnedPaymentIntent
    waitForGatewayConfirmation(returnedPaymentIntent).catch((error) => toast.error(getErrorMessage(error)))
  }, [returnedPaymentIntent])

  if (paymentQuery.isLoading) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <div className="mx-auto max-w-2xl space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </main>
    )
  }

  if (paymentQuery.isError || !paymentDetail) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <Card className="mx-auto max-w-2xl">
          <CardHeader><CardTitle>Payment link unavailable</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground">This payment link is invalid or no longer available.</CardContent>
        </Card>
      </main>
    )
  }

  const currencyCode = order?.currency_code || undefined
  const total = toNumber(order?.total)
  const balance = toNumber(order?.balance || payment?.amount)

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-2xl space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              {paid ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <ReceiptText className="h-5 w-5" />}
              Order payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Order</span>
              <span className="font-medium">#{order?.reference_id || order?.id}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Total</span>
              <span>{formatCurrency(total, currencyCode)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Balance</span>
              <span className="font-semibold">{formatCurrency(balance, currencyCode)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {paid ? (
              <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <p className="font-medium">Payment has been confirmed.</p>
                {payment?.payment_reference_id && (
                  <p className="break-all text-xs text-emerald-700">Payment reference {payment.payment_reference_id}</p>
                )}
              </div>
            ) : confirmationPending ? (
              <div className="flex flex-col items-center gap-4 rounded-md border border-amber-200 bg-amber-50 px-6 py-10 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-amber-700" />
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-amber-950">Payment completed</h2>
                  <p className="max-w-md text-sm text-amber-800">
                    Waiting for gateway confirmation. This page will update automatically once the payment is verified.
                  </p>
                  {pendingPaymentReferenceId && (
                    <p className="break-all text-xs text-amber-700">Reference {pendingPaymentReferenceId}</p>
                  )}
                </div>
              </div>
            ) : isStripe && stripePromise && stripeElementsOptions ? (
              <Elements stripe={stripePromise} options={stripeElementsOptions}>
                <StripeOrderPaymentForm paymentReferenceId={payment?.payment_reference_id} onPaymentSucceeded={waitForGatewayConfirmation} />
              </Elements>
            ) : isCashFree && payment?.payment_link ? (
              <Button className="w-full" size="lg" onClick={() => window.location.assign(payment.payment_link!)}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Continue to payment
              </Button>
            ) : (
              <div className="rounded-md border p-4 text-sm text-muted-foreground">Payment gateway details are unavailable.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}